import { concurrentQueue } from "@/utils";

interface GetAllShopDataByOffsetPaginationProps<T> {
    shopIds: number[];
    shopTotalCountMapping: Map<number, number>;
    pageNo: number;
    pageSize: number;
    /** 请求接口的最大数量 */
    maxLimit?: number;
    /** 请求数据发生错误时是否中止整个任务 */
    abortOnError?: boolean;
    /** 店铺加载失败回调 */
    onShopLoadError?: (shopId: number, error: Error) => void;
    /** 店铺加载数据回调 */
    onLoadShopData: (shopId: number, offset: number, limit: number) => Promise<T[]>;
}

export async function getAllShopDataByOffsetPagination<T>(props: GetAllShopDataByOffsetPaginationProps<T>) {
    const {
        shopIds,
        shopTotalCountMapping,
        pageNo,
        pageSize,
        onShopLoadError,
        onLoadShopData,
        maxLimit = 200,
        abortOnError,
    } = props;
    const offset = pageSize * (pageNo - 1);
    const needLoadOrderCount = pageSize;

    const items: T[] = [];

    let startIndex = 0;
    const shopOffsetMapping = new Map<number, number>();

    let beforeCount = 0;
    for (let i = 0; i < shopIds.length; i++) {
        const shopId = shopIds[i];
        const currentTotalCount = shopTotalCountMapping.get(shopId) || 0;
        const afterCount = beforeCount + currentTotalCount;

        // 先移动偏移到接近的位置
        if (beforeCount <= offset && afterCount >= offset) {
            startIndex = i;

            const currentShopOffset = offset - beforeCount;

            shopOffsetMapping.set(shopId, currentShopOffset);

            break;
        }

        beforeCount += currentTotalCount;
    }

    for (; startIndex < shopIds.length; startIndex++) {
        const shopId = shopIds[startIndex];

        const currentShopOrderTotalCount = shopTotalCountMapping.get(shopId) || 0;

        // 如果当前店铺数据是空的,继续往下走
        if (!currentShopOrderTotalCount) {
            continue;
        }

        // 当前店铺需要加载的条数
        const currentShopNeedLoadOrderCount = needLoadOrderCount - items.length;

        if (currentShopNeedLoadOrderCount <= 0) {
            break;
        }

        const limit = Math.min(currentShopOrderTotalCount, currentShopNeedLoadOrderCount, maxLimit);

        // 查询当前店铺的偏移
        const offset = shopOffsetMapping.get(shopId) || 0;

        try {
            const result = await onLoadShopData(shopId, offset, limit);

            items.push(...result);

            // 如果店铺的数据还没有加载完，则继续加载该店铺的数据，并且重新设置偏移
            if (offset + result.length < currentShopOrderTotalCount) {
                startIndex--;

                shopOffsetMapping.set(shopId, offset + result.length);

                continue;
            }
        } catch (error) {
            onShopLoadError?.(shopId, error);

            if (abortOnError) {
                break;
            }
        }
    }

    return items;
}

interface GetAllShopDataByOffsetPaginationByConcurrentProps<T> extends GetAllShopDataByOffsetPaginationProps<T> {
    onLoadedCountChange?(count: number): void;
}

/**
 * 并发模式获取订单列表
 */
export async function getAllShopDataByOffsetPaginationByConcurrent<T>(
    props: GetAllShopDataByOffsetPaginationByConcurrentProps<T>,
) {
    const {
        shopIds,
        shopTotalCountMapping,
        pageNo,
        pageSize,
        onShopLoadError,
        onLoadShopData,
        maxLimit = 200,
        onLoadedCountChange,
    } = props;

    const offset = pageSize * (pageNo - 1);
    const needLoadOrderCount = pageSize;

    let allShopOrderCount = 0;
    shopTotalCountMapping.forEach((count) => {
        allShopOrderCount += count;
    });

    if (offset > allShopOrderCount) {
        return [];
    }

    let startIndex = 0;
    const shopOffsetMapping = new Map<number, number>();

    let beforeCount = 0;
    for (let i = 0; i < shopIds.length; i++) {
        const shopId = shopIds[i];
        const currentTotalCount = shopTotalCountMapping.get(shopId) || 0;
        const afterCount = beforeCount + currentTotalCount;

        // 先移动偏移到接近的位置
        if (beforeCount < offset && afterCount >= offset) {
            startIndex = i;

            const currentShopOffset = offset - beforeCount;

            shopOffsetMapping.set(shopId, currentShopOffset);

            break;
        }

        beforeCount += currentTotalCount;
    }

    const needLoadShopOrdersInfos: {
        shopId: number;
        offset: number;
        limit: number;
        dataSource: T[];
    }[] = [];

    /**
     * 已加载到的订单数量
     */
    let loadedOrderCount = 0;

    for (; startIndex < shopIds.length; startIndex++) {
        const shopId = shopIds[startIndex];

        const currentShopOrderTotalCount = shopTotalCountMapping.get(shopId) || 0;

        // 如果当前店铺数据是空的,继续往下走
        if (!currentShopOrderTotalCount) {
            continue;
        }

        // 查询当前店铺的偏移
        let startOffset = shopOffsetMapping.get(shopId) || 0;

        // 当前店铺剩余的订单数量
        const currentShopOrderCountRemainder = currentShopOrderTotalCount - startOffset;

        if (currentShopOrderCountRemainder <= 0) {
            break;
        }

        // 剩余需要加载的订单数量
        const remainderNeedLoadOrderCount = needLoadOrderCount - loadedOrderCount;

        // 没有剩余要加载的订单量跳出循环即可
        if (remainderNeedLoadOrderCount <= 0) {
            break;
        }

        // 当前当铺实际应该需要加载的订单数量
        const currentShopOrderShouldLoadCount = Math.min(currentShopOrderCountRemainder, remainderNeedLoadOrderCount);

        // 限制每次的订单加载量
        const limit = Math.min(currentShopOrderShouldLoadCount, maxLimit);

        // 当前店铺根据limit计算需要加载几次
        const count = Math.floor(currentShopOrderShouldLoadCount / limit);

        // 加载完后，还剩多少
        const remainder = currentShopOrderShouldLoadCount % limit;

        for (let i = 0; i < count; i++) {
            needLoadShopOrdersInfos.push({
                shopId,
                offset: startOffset,
                limit: limit,
                dataSource: [],
            });

            startOffset += limit;
            loadedOrderCount += limit;
        }

        if (remainder) {
            needLoadShopOrdersInfos.push({
                shopId,
                offset: startOffset,
                limit: remainder,
                dataSource: [],
            });

            startOffset += remainder;
            loadedOrderCount += remainder;
        }
    }

    const items: T[] = [];
    let realLoadOrderCount = 0;
    await concurrentQueue({
        data: needLoadShopOrdersInfos,
        maxTask: 5,
        async callback(orderInfo) {
            const { shopId, limit, offset } = orderInfo;

            try {
                const list = await onLoadShopData(shopId, offset, limit);

                orderInfo.dataSource = list;

                realLoadOrderCount += list.length;

                onLoadedCountChange?.(realLoadOrderCount);
            } catch (error) {
                onShopLoadError?.(shopId, error);
            }
        },
    });

    for (let i = 0; i < needLoadShopOrdersInfos.length; i++) {
        const ordersInfo = needLoadShopOrdersInfos[i];

        items.push(...ordersInfo.dataSource);
    }

    return items;
}
